/**
 * DPL DEW — Tenant Service
 *
 * Handles tenant creation, user onboarding, and current-session
 * tenant resolution. This is the entry point for new company signups.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Json } from '@/integrations/supabase/types';
import { refreshIndustryCache } from './industryService';
import { settingsService } from './settingsService';
import { logger } from '@/lib/utils/logger';
import {
  DEFAULT_INDUSTRY_SLUG,
  normalizeIndustrySlug,
  normalizeIndustrySlugs,
  CANONICAL_INDUSTRY_SLUGS,
} from '@/data/industryOptions';

export type Tenant = Database['public']['Tables']['tenants']['Row'] & {
  [key: string]: any
};
export type TenantUser = Database['public']['Tables']['tenant_users']['Row'];

interface OnboardTenantResult {
  status: string;
  industries: string[];
  project_types: string[];
  catalog_items: number;
  error?: string;
}

interface CreateQuoteResult {
  status: string;
  quote_id: string;
  quote_number: string;
  section_count: number;
  scope_count: number;
  title?: string;
  error?: string;
}

interface TenantSetupOptions {
  accountType?: 'company' | 'individual';
  industrySlug?: string;
}

interface TenantCreationRpcResult {
  success?: boolean;
  tenant_id?: string;
  tenant_name?: string;
  tenant_slug?: string;
  user_role?: string;
  error?: string;
}

// ── Cache ─────────────────────────────────────────────────────
let _currentTenant: Tenant | null = null;
let _currentRole:   string | null = null;
let _cachedUserId:  string | null = null;

// ── Core ──────────────────────────────────────────────────────

/**
 * Get the current user's tenant.
 * Returns null if the user has no tenant assigned yet (needs onboarding).
 */
export async function getCurrentTenant(): Promise<Tenant | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    clearTenantCache();
    return null;
  }

  // If cached user is different, clear cache first
  if (_cachedUserId && _cachedUserId !== session.user.id) {
    logger.log('[Tenant] Different user detected — clearing cache');
    clearTenantCache();
  }

  if (_currentTenant && _cachedUserId === session.user.id) {
    return _currentTenant;
  }

  logger.log(`[Tenant] Getting tenant for user: ${session.user.email} (id: ${session.user.id})`);

  try {
    // Check if user is admin first
    const { data: isAdminData, error: adminError } = await supabase.rpc('is_master_admin' as any);
    
    if (!adminError && isAdminData) {
      logger.log('[Tenant] User is master admin - no tenant context');
      _currentTenant = null;
      _currentRole = 'master_admin';
      _cachedUserId = session.user.id;
      return null; // Admins don't have tenants
    }
    
    // Get user context to determine tenant
    const { data: contextData, error: contextError } = await supabase.rpc('get_auth_context' as any);
    
    if (contextError) {
      logger.warn('[Tenant] get_auth_context failed:', contextError);
      return null;
    }
    
    // get_auth_context returns a single JSONB object, not an array
    const context = contextData as any;
    if (!context || context.role === 'error') {
      logger.log('[Tenant] No valid user context found');
      return null;
    }
    
    // If user is admin, return null
    if (context.role === 'admin') {
      logger.log('[Tenant] User is admin - no tenant context');
      _currentTenant = null;
      _currentRole = 'master_admin';
      _cachedUserId = session.user.id;
      return null;
    }
    
    // If user needs onboarding, return null
    if (context.role === 'unassigned') {
      logger.log('[Tenant] User needs onboarding - no tenant yet');
      _currentTenant = null;
      _currentRole = null;
      _cachedUserId = session.user.id;
      return null;
    }
    
    // Return tenant context
    logger.log(`[Tenant] User has tenant: ${context.tenant_id}`);
    const tenantRecord = await fetchTenantRecord(context.tenant_id);
    _currentTenant = tenantRecord ?? {
      id: context.tenant_id,
      name: context.tenant_name || 'Unknown',
      slug: context.tenant_slug || context.tenant_name?.toLowerCase().replace(/[^a-z0-9-]/g, '-') || 'workspace',
      plan: context.tenant_plan || 'starter',
      status: context.tenant_status || 'active',
      account_type: 'company',
      billing_email: null,
      primary_industry_slug: null,
      stripe_customer_id: null,
      subscription_expiry_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    _currentRole = context.user_role || 'member';
    _cachedUserId = session.user.id;
    return _currentTenant;
    
  } catch (error) {
    logger.warn('[Tenant] getCurrentTenant failed:', error);
    return null;
  }
}

export async function getCurrentRole(): Promise<string | null> {
  if (_currentRole) return _currentRole;
  await getCurrentTenant(); // populates _currentRole as a side-effect
  return _currentRole;
}

/**
 * Sanitize a workspace slug to enforce production slug rules:
 *   - Lowercase only
 *   - Spaces → hyphens
 *   - Special chars removed
 *   - No consecutive hyphens
 *   - Max 30 characters
 *   - Cannot be empty (falls back to 'workspace')
 */
function sanitizeSlug(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')          // spaces/underscores → hyphen
    .replace(/[^a-z0-9-]/g, '')        // remove anything else
    .replace(/-{2,}/g, '-')            // no consecutive hyphens
    .replace(/^-+|-+$/g, '')           // trim leading/trailing hyphens
    .slice(0, 30)
    || 'workspace';
}

/**
 * Create a new tenant and assign the current user as owner.
 *
 * Idempotent: if the user already owns a tenant (detected via tenant_users),
 * returns that tenant immediately without creating a duplicate.
 *
 * Slug collision handling: retries up to MAX_SLUG_RETRIES times, appending
 * a 4-char random suffix on each collision (§6 of onboarding spec).
 */
async function fetchTenantRecord(tenantId: string): Promise<Tenant | null> {
  const { data, error } = await supabase
    .from('tenants')
    .select('*')
    .eq('id', tenantId)
    .maybeSingle();

  if (error || !data) return null;
  return data as Tenant;
}

async function createTenantAssociation(
  name: string,
  slug: string,
): Promise<string> {
  const attempts: Array<{
    rpc: string;
    args: Record<string, unknown>;
  }> = [
    {
      rpc: 'create_tenant_for_user',
      args: { tenant_name: name, tenant_slug: slug, user_role: 'owner' },
    },
    {
      rpc: 'setup_create_tenant',
      args: { tenant_name: name, tenant_slug: slug, user_role: 'owner' },
    },
    {
      rpc: 'fn_onboard_tenant',
      args: { tenant_name: name, tenant_slug: slug, user_role: 'owner' },
    },
  ];

  const errors: string[] = [];

  for (const attempt of attempts) {
    const { data, error } = await supabase.rpc(attempt.rpc as any, attempt.args as any);

    if (error) {
      errors.push(`${attempt.rpc}: ${error.message}`);
      continue;
    }

    if (typeof data === 'string' && data) {
      return data;
    }

    const result = (data ?? {}) as TenantCreationRpcResult;
    if (result.error) {
      errors.push(`${attempt.rpc}: ${result.error}`);
      continue;
    }

    if (result.success && result.tenant_id) {
      return result.tenant_id;
    }
  }

  throw new Error(
    errors.length > 0
      ? `Tenant creation failed. ${errors.join(' | ')}`
      : 'Tenant creation failed.',
  );
}

async function finalizeTenantSetup(
  tenantId: string,
  plan: string,
  options: TenantSetupOptions = {},
): Promise<Tenant> {
  const normalizedIndustrySlug = normalizeIndustrySlug(
    options.industrySlug ?? DEFAULT_INDUSTRY_SLUG,
  );
  const accountType = options.accountType ?? 'company';

  const { error: tenantUpdateError } = await supabase
    .from('tenants')
    .update({
      plan,
      account_type: accountType,
      primary_industry_slug: normalizedIndustrySlug,
      updated_at: new Date().toISOString(),
    })
    .eq('id', tenantId);

  if (tenantUpdateError) {
    throw new Error(tenantUpdateError.message);
  }

  const { data: existingIndustries, error: industryError } = await supabase
    .from('industries')
    .select('slug')
    .eq('tenant_id', tenantId);

  if (industryError) {
    throw new Error(industryError.message);
  }

  const hasSelectedIndustry = (existingIndustries ?? []).some(
    (industry) => industry.slug === normalizedIndustrySlug,
  );

  if (!hasSelectedIndustry) {
    // We only link the industry to the tenant. We NO LONGER auto-seed starter catalogues.
    // Starter catalog seeding is now an explicit user choice in OnboardingFlow.tsx.
    const { error: insertError } = await supabase
      .from('industries')
      .insert({
        slug: normalizedIndustrySlug,
        name: normalizedIndustrySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        tenant_id: tenantId,
        is_system: false,
      });
      
    if (insertError) {
      logger.warn('[Tenant] Failed to link industry, but continuing setup:', insertError);
    }
  }

  const hydratedTenant = await fetchTenantRecord(tenantId);
  if (!hydratedTenant) {
    throw new Error('Tenant created but could not be reloaded.');
  }

  return hydratedTenant;
}

export async function createTenant(
  name: string,
  slug: string,
  plan: string = 'starter',
  options: TenantSetupOptions = {},
): Promise<Tenant> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Must be signed in to create a tenant');

  const userId = session.user.id;
  logger.log(`[Tenant] createTenant attempt — user: ${userId}, name: "${name}", slug: "${slug}"`);

  // ── Idempotency guard (§7) ────────────────────────────────────────────────
  // Check if user already has a tenant by trying to get current tenant first
  const normalizedIndustrySlug = normalizeIndustrySlug(
    options.industrySlug ?? DEFAULT_INDUSTRY_SLUG,
  );
  const existingTenant = await getCurrentTenant();
  if (existingTenant) {
    logger.log(`[Tenant] User already has tenant ${existingTenant.id} — skipping creation`);
    const repairedTenant = await finalizeTenantSetup(existingTenant.id, plan, {
      ...options,
      industrySlug: normalizedIndustrySlug,
    });
    _currentTenant = repairedTenant;
    _currentRole = 'owner';
    _cachedUserId = userId;
    return repairedTenant;
  }

  // ── Slug sanitization (§9 — backend always re-sanitizes) ─────────────────
  const baseSlug = sanitizeSlug(slug);
  logger.log(`[Tenant] Sanitized slug: "${slug}" → "${baseSlug}"`);

  // ── Use fn_onboard_tenant function instead of direct table access ─────────────────
  try {
    const tenantId = await createTenantAssociation(name, baseSlug);
    const tenant = await finalizeTenantSetup(tenantId, plan, {
      ...options,
      industrySlug: normalizedIndustrySlug,
    });
    _currentTenant = tenant;
    _currentRole = 'owner';
    _cachedUserId = userId;

    try {
      await supabase
        .from('tenant_settings')
        .upsert(
          [
            {
              tenant_id: tenant.id,
              key: 'app_preferences',
              value: {
                quote_number_prefix: baseSlug.toUpperCase().slice(0, 6),
                default_validity_days: 30,
                default_gst_rate: 18,
                currency: 'INR',
                date_format: 'dd/MM/yyyy',
              } as Json,
            },
            {
              tenant_id: tenant.id,
              key: 'company_profile',
              value: {
                name,
                legal_name: name,
                industries: [normalizedIndustrySlug],
              } as Json,
            },
          ],
          { onConflict: 'tenant_id,key' },
        );
    } catch (settingsError) {
      logger.warn('[Tenant] Failed to seed tenant settings (non-fatal):', settingsError);
    }

    return tenant;

  } catch (error) {
    logger.error('[Tenant] Tenant creation failed:', error);
    throw new Error(error instanceof Error ? error.message : String(error));
  }
}

/**
 * Onboard the current tenant with selected industries.
 * Clones system templates + catalogue items into the tenant's workspace.
 * Returns the result from fn_onboard_tenant.
 */
export async function onboardTenant(
  tenantId: string,
  industrySlugs: string[],
): Promise<{
  status: string;
  industries:    string[];
  project_types: string[];
  catalog_items: number;
  error?:        string;
}> {
  const normalizedIndustrySlugs = normalizeIndustrySlugs(industrySlugs);

  // Runtime validation of canonical industry slugs
  for (const slug of normalizedIndustrySlugs) {
    if (!CANONICAL_INDUSTRY_SLUGS.includes(slug)) {
      const err = new Error(`Invalid industry slug: ${slug}`);
      console.error('[Industry Onboarding Failed]', {
        tenantId,
        slugs: normalizedIndustrySlugs,
        error: err.message,
      });
      throw err;
    }
  }

  const { data, error } = await supabase.rpc('fn_onboard_tenant', {
    p_tenant_id:      tenantId,
    p_industry_slugs: normalizedIndustrySlugs,
  });

  if (error) {
    console.error('[Industry Onboarding Failed]', {
      tenantId,
      slugs: normalizedIndustrySlugs,
      error: error.message,
    });
    throw new Error(error.message);
  }

  const result = data as unknown as OnboardTenantResult;
  if (result.status === 'error') {
    console.error('[Industry Onboarding Failed]', {
      tenantId,
      slugs: normalizedIndustrySlugs,
      error: result.error,
    });
    throw new Error(result.error);
  }

  // Bust caches after onboarding
  refreshIndustryCache();
  settingsService.initialize().catch(() => {});

  return result;
}

/**
 * Create a new quote using the database function.
 * Returns the new quote_id and quote_number.
 */
export async function createQuoteFromTemplate(params: {
  projectTypeId:    string;
  clientInfo:       Json;
  scopeSelections:  { type_value: string; count: number }[];
}): Promise<{ quote_id: string; quote_number: string; section_count: number; scope_count: number; title: string }> {
  const tenant = await getCurrentTenant();
  if (!tenant) throw new Error('No tenant — cannot create quote');

  const { data, error } = await supabase.rpc('fn_create_quote_from_template', {
    p_tenant_id:        tenant.id,
    p_project_type_id:  params.projectTypeId,
    p_client_info:      params.clientInfo,
    p_scope_selections: params.scopeSelections as unknown as Json,
  });

  if (error) throw new Error(error.message);

  const result = data as unknown as CreateQuoteResult;
  if (result.status === 'error') throw new Error(result.error ?? 'Quote creation failed');

  return {
    quote_id:      result.quote_id,
    quote_number:  result.quote_number,
    title:         result.title || 'Untitled Quote',
    section_count: result.section_count,
    scope_count:   result.scope_count,
  };
}

/** Bust cache (call on sign-out) */
export function clearTenantCache(): void {
  _currentTenant = null;
  _currentRole   = null;
  _cachedUserId  = null;
  settingsService.resetCache();
  refreshIndustryCache();
}

export interface TenantMember {
  user_id: string;
  email: string;
  fullName: string;
  role: string;
  joinedAt: string;
}

/**
 * Get all members of the current tenant.
 */
export async function getTenantMembers(): Promise<TenantMember[]> {
  const tenant = await getCurrentTenant();
  if (!tenant) return [];

  const { data, error } = await supabase
    .from('tenant_users')
    .select(`
      user_id,
      role,
      created_at,
      profiles (
        email,
        full_name
      )
    `)
    .eq('tenant_id', tenant.id);

  if (error) throw new Error(error.message);

  return (data || []).map((row: { user_id: string; role: string; created_at: string; profiles?: { email?: string; full_name?: string } }) => ({
    user_id: row.user_id,
    email: row.profiles?.email || 'N/A',
    fullName: row.profiles?.full_name || 'Anonymous',
    role: row.role,
    joinedAt: row.created_at,
  }));
}

/**
 * Add a new member to the tenant by email.
 * The user must already have a profile in the system.
 */
export async function addTenantMemberByEmail(email: string, role: string = 'member'): Promise<void> {
  const tenant = await getCurrentTenant();
  if (!tenant) throw new Error('No active tenant context');

  // Plan Check: Starter plan is solo-only
  if (tenant.plan === 'starter') {
    throw new Error('Starter Plan is solo-only. Please upgrade to Pro to add colleagues.');
  }

  // 1. Resolve email to user_id via profiles
  const { data: profile, error: profErr } = await supabase
    .from('profiles')
    .select('id')
    .eq('email', email.trim().toLowerCase())
    .maybeSingle();

  if (profErr) throw new Error(profErr.message);
  if (!profile) throw new Error(`User with email "${email}" not found. Ask them to sign up first.`);

  // 2. Add to tenant_users
  const { error: joinErr } = await supabase
    .from('tenant_users')
    .insert({
      tenant_id: tenant.id,
      user_id:   profile.id,
      role:      role
    });

  if (joinErr) {
    if (joinErr.code === '23505') throw new Error('User is already a member of this team.');
    throw new Error(joinErr.message);
  }
}

/**
 * Remove a member from the tenant.
 */
export async function removeTenantMember(userId: string): Promise<void> {
  const tenant = await getCurrentTenant();
  if (!tenant) throw new Error('No active tenant context');

  const { error } = await supabase
    .from('tenant_users')
    .delete()
    .eq('tenant_id', tenant.id)
    .eq('user_id', userId);

  if (error) throw new Error(error.message);
}

export const tenantService = {
  getCurrentTenant,
  getCurrentRole,
  createTenant,
  onboardTenant,
  createQuoteFromTemplate,
  getTenantMembers,
  addTenantMemberByEmail,
  removeTenantMember,
  clearCache: clearTenantCache,
};
